import type { ReactNode } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { Icon } from '@/app_components/common/Icon';
import { ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';

const affiliateProductCard = tv(
  {
    defaultVariants: {
      featuredProduct: false,
      hasTitle: true,
      layout: 'default',
    },

    slots: {
      bodyWrapper: '',
      cardWrapper: 'not-prose flex flex-col border w-full shadow-sm rounded-sm overflow-hidden',
      contentWrapper: 'grid layout-grid-gap md:grid-cols-2 md:grid-rows-1',
      header: 'typography-default font-medium flex flex-row px-xs md:px-sm py-xxs gap-lg items-center',
      productName: 'font-semibold typography-level-4',
      promotion: 'rounded-sm p-xxs success-surface relative self-stretch',
    },
    variants: {
      featuredProduct: {
        false: {
          cardWrapper: 'stroke-secondary',
          header: 'bg-blue-50 text-blue-800',
        },
        true: {
          cardWrapper: 'stroke-accent',
          header: 'bg-gradient-to-r from-blue-400 to-blue-600 text-white',
        },
      },
      hasTitle: {
        false: {
          header: 'justify-end',
        },
        true: {
          header: 'justify-between',
        },
      },
      layout: {
        default: {
          cardWrapper: '',
        },
      },
    },
  },
  { twMerge: true },
);

const STAR_SIZE_MEDIUM = 24;
const STAR_SIZE_SMALL = 12;

function getStarState(rating: number, starValue: number): 'full' | 'half' | 'empty' {
  if (rating >= starValue) {
    return 'full';
  }
  if (rating >= starValue - 0.5) {
    return 'half';
  }
  return 'empty';
}

export type AffiliateProductCardVariants = VariantProps<typeof affiliateProductCard>;

export const AffiliateProductCardWrapper = ({
  layout,
  children,
  className = '',
  featuredProduct = false,
}: {
  children: ReactNode;
  className?: string;
} & AffiliateProductCardVariants) => {
  return (
    <div
      className={cn(affiliateProductCard().cardWrapper({ featuredProduct, layout }), className)}
      data-cy={ContentPlacement.AffiliateProductCardWrapper}
    >
      {children}
    </div>
  );
};

export const ProductCardHeader = ({
  hasTitle = true,
  layout,
  children,
  className = '',
  featuredProduct = false,
}: {
  children: ReactNode;
  className?: string;
} & AffiliateProductCardVariants) => {
  return (
    <div
      className={cn(affiliateProductCard().header({ featuredProduct, hasTitle, layout }), className)}
      data-cy={ContentPlacement.AffiliateProductCardHeader}
    >
      {children}
    </div>
  );
};

export const ProductCardContentWrapper = ({
  layout,
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
} & AffiliateProductCardVariants) => {
  return (
    <div
      className={cn(affiliateProductCard().contentWrapper({ layout }), className)}
      data-cy={ContentPlacement.AffiliateProductCardContentWrapper}
    >
      {children}
    </div>
  );
};

export const ProductCardProductName = ({
  layout,
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
} & AffiliateProductCardVariants) => {
  return (
    <h2
      className={cn(affiliateProductCard().productName({ layout }), className, 'text-level-4')}
      data-cy={ContentPlacement.AffiliateProductCardProductName}
    >
      {children}
    </h2>
  );
};

export const ProductCardBodyWrapper = ({
  layout,
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
} & AffiliateProductCardVariants) => {
  return (
    <div
      className={cn(affiliateProductCard().bodyWrapper({ layout }), className)}
      data-cy={ContentPlacement.AffiliateProductCardBodyWrapper}
    >
      {children}
    </div>
  );
};

export const ProductCardPromotionWrapper = ({
  layout,
  children,
  className = '',
}: {
  children: ReactNode;
  className?: string;
} & AffiliateProductCardVariants) => {
  return <div className={cn(affiliateProductCard().promotion({ layout }), className)}>{children}</div>;
};

// TODO: Merge the logic between this Rating Component and app/_components/widgets/RatesWidget/StarRating.tsx
export const AffiliateProductCardRating = ({
  rating,
  size = 'medium',
  collapsible = false,
  maxRating = 5,
}: {
  rating: number;
  size?: 'small' | 'medium';
  collapsible?: boolean;
  maxRating?: number;
}) => {
  const starSize = size === 'small' ? STAR_SIZE_SMALL : STAR_SIZE_MEDIUM;
  const starIconProps = {
    height: starSize,
    size: 'custom' as const,
    strokeWidth: 1.5,
    width: starSize,
  };

  return (
    <div className={cn('flex gap-1 content-center', size === 'small' ? 'items-center' : 'items-end')}>
      <span className='font-graphik'>{rating.toFixed(1)}</span>
      {Array.from({ length: maxRating }, (_, i) => i + 1).map((value) => {
        const state = getStarState(rating, value);
        const filled = state === 'full';
        const half = state === 'half';

        if (half) {
          return (
            <div
              className={cn(
                'relative flex',
                size === 'small' ? 'size-3' : 'size-6',
                collapsible && value > 1 && 'hidden md:flex',
              )}
              key={value}
            >
              <Icon
                {...starIconProps}
                className='absolute stroke-yellow-400'
                icon='Star'
                mode='custom'
              />
              <Icon
                {...starIconProps}
                className='absolute text-yellow-400'
                icon='StarHalf'
                mode='fill'
              />
            </div>
          );
        }

        return (
          <div
            className={cn(collapsible && value > 1 && 'hidden md:flex')}
            key={value}
          >
            <Icon
              {...starIconProps}
              className={cn(filled ? 'fill-yellow-400 stroke-yellow-400' : 'stroke-yellow-400')}
              icon='Star'
              mode='custom'
            />
          </div>
        );
      })}
    </div>
  );
};
